import { useCallback, useEffect, useRef, useState } from "react";
import { ordersAnalyticsApi } from "@/features/orders-analytics/api/orders-analytics.api";
import {
    OrdersAnalyticsFilters,
    OrdersCohortsResponse,
    OrdersCustomersResponse,
    OrdersDashboardResponse,
    OrdersDrilldownResponse,
    OrdersFunnelResponse,
    OrdersMonetizationResponse,
    OrdersPerformanceResponse,
    OrdersQualityResponse,
    OrdersSegmentationResponse,
    OrdersSummaryResponse,
    OrdersTodayResponse,
    OrdersTrendsResponse,
    SegmentationDimension,
} from "@/features/orders-analytics/types/orders-analytics.types";
import { timezone } from "@/features/orders-analytics/utils/timezone";

type DataState = {
    availableClients: string[];
    availableDestinationRails: string[];
    summary: OrdersSummaryResponse | null;
    trends: OrdersTrendsResponse | null;
    today: OrdersTodayResponse | null;
    customers: OrdersCustomersResponse | null;
    cohorts: OrdersCohortsResponse | null;
    performance: OrdersPerformanceResponse | null;
    monetization: OrdersMonetizationResponse | null;
    quality: OrdersQualityResponse | null;
    segmentation: OrdersSegmentationResponse | null;
    orders: OrdersDrilldownResponse | null;
    funnel: OrdersFunnelResponse | null;
};

const initialState: DataState = {
    availableClients: [],
    availableDestinationRails: [],
    summary: null,
    trends: null,
    today: null,
    customers: null,
    cohorts: null,
    performance: null,
    monetization: null,
    quality: null,
    segmentation: null,
    orders: null,
    funnel: null,
};

const toDataState = (
    dashboard: OrdersDashboardResponse,
    orders: OrdersDrilldownResponse
): DataState => ({
    availableClients: dashboard.filterOptions !== null ? dashboard.filterOptions.clients : [],
    availableDestinationRails:
        dashboard.filterOptions !== null ? dashboard.filterOptions.destinationRails : [],
    summary: dashboard.summary,
    trends: dashboard.trends,
    today: dashboard.today,
    customers: dashboard.customers,
    cohorts: dashboard.cohorts,
    performance: dashboard.performance,
    monetization: dashboard.monetization,
    quality: dashboard.quality,
    segmentation: dashboard.segmentation,
    orders,
    funnel: dashboard.funnel,
});

export function useOrdersAnalyticsData(
    filters: OrdersAnalyticsFilters,
    segmentationDimension: SegmentationDimension,
    ordersDayTimestamp: number,
    ordersEmailSearch: string | null,
    ordersProblemsPage: number | null,
    ordersCancelledPage: number | null = null
) {
    const [data, setData] = useState<DataState>(initialState);
    const [loading, setLoading] = useState(true);
    const [isFetching, setIsFetching] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshToken, setRefreshToken] = useState<string | null>(null);
    const [exportLoading, setExportLoading] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);
    const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);

    const dashboardCacheRef = useRef(
        new Map<string, { data: OrdersDashboardResponse; lastRefreshedAt: number }>()
    );
    const drilldownCacheRef = useRef(new Map<string, OrdersDrilldownResponse>());
    const dashboardInFlightRef = useRef(new Map<string, Promise<OrdersDashboardResponse>>());
    const drilldownInFlightRef = useRef(new Map<string, Promise<OrdersDrilldownResponse>>());
    const requestIdRef = useRef(0);
    const hasLoadedOnceRef = useRef(false);
    const consumedRefreshTokenRef = useRef<string | null>(null);

    useEffect(() => {
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;

        const dashboardKey = JSON.stringify({ filters, segmentationDimension });
        const drilldownKey = JSON.stringify({
            filters,
            ordersDayTimestamp,
            ordersEmailSearch,
            ordersProblemsPage,
            ordersCancelledPage,
        });

        const forceRefresh =
            refreshToken !== null && refreshToken !== consumedRefreshTokenRef.current;

        if (forceRefresh) {
            consumedRefreshTokenRef.current = refreshToken;
        }

        const cachedDashboard = forceRefresh
            ? undefined
            : dashboardCacheRef.current.get(dashboardKey);
        const cachedDrilldown = forceRefresh
            ? undefined
            : drilldownCacheRef.current.get(drilldownKey);

        setError(null);

        if (cachedDashboard !== undefined && cachedDrilldown !== undefined) {
            setData(toDataState(cachedDashboard.data, cachedDrilldown));
            setLastRefreshedAt(cachedDashboard.lastRefreshedAt);
            setLoading(false);
            setIsFetching(false);
            hasLoadedOnceRef.current = true;
            return;
        }

        setLoading(!hasLoadedOnceRef.current);
        setIsFetching(true);

        const getDashboard = (): Promise<OrdersDashboardResponse> => {
            if (cachedDashboard !== undefined) {
                return Promise.resolve(cachedDashboard.data);
            }
            const existing = forceRefresh
                ? undefined
                : dashboardInFlightRef.current.get(dashboardKey);
            const promise =
                existing ?? ordersAnalyticsApi.fetchOrdersDashboard(filters, segmentationDimension);
            if (existing === undefined) {
                dashboardInFlightRef.current.set(dashboardKey, promise);
                const cleanup = () => {
                    if (dashboardInFlightRef.current.get(dashboardKey) === promise) {
                        dashboardInFlightRef.current.delete(dashboardKey);
                    }
                };
                void promise.then(cleanup, cleanup);
            }
            return promise;
        };

        const getDrilldown = (): Promise<OrdersDrilldownResponse> => {
            if (cachedDrilldown !== undefined) {
                return Promise.resolve(cachedDrilldown);
            }
            const existing = forceRefresh
                ? undefined
                : drilldownInFlightRef.current.get(drilldownKey);
            const promise =
                existing ??
                (ordersProblemsPage !== null
                    ? ordersAnalyticsApi.fetchProblemOrders(filters, ordersProblemsPage)
                    : ordersCancelledPage !== null
                      ? ordersAnalyticsApi.fetchCancelledOrders(filters, ordersCancelledPage)
                      : ordersEmailSearch !== null
                      ? ordersAnalyticsApi.fetchOrdersByEmail(filters, ordersEmailSearch)
                      : ordersAnalyticsApi.fetchOrdersDrilldown(
                            filters,
                            ordersDayTimestamp,
                            timezone.shiftTimeZoneDays(ordersDayTimestamp, 1) - 1
                        ));
            if (existing === undefined) {
                drilldownInFlightRef.current.set(drilldownKey, promise);
                const cleanup = () => {
                    if (drilldownInFlightRef.current.get(drilldownKey) === promise) {
                        drilldownInFlightRef.current.delete(drilldownKey);
                    }
                };
                void promise.then(cleanup, cleanup);
            }
            return promise;
        };

        Promise.all([getDashboard(), getDrilldown()])
            .then(([dashboard, orders]) => {
                if (requestIdRef.current !== requestId) {
                    return;
                }

                const refreshedAt = cachedDashboard?.lastRefreshedAt ?? Date.now();
                if (cachedDashboard === undefined) {
                    dashboardCacheRef.current.set(dashboardKey, {
                        data: dashboard,
                        lastRefreshedAt: refreshedAt,
                    });
                }
                drilldownCacheRef.current.set(drilldownKey, orders);

                setData(toDataState(dashboard, orders));
                setLastRefreshedAt(refreshedAt);
                setLoading(false);
                setIsFetching(false);
                hasLoadedOnceRef.current = true;
            })
            .catch((reason) => {
                if (requestIdRef.current !== requestId) {
                    return;
                }

                setError(reason instanceof Error ? reason.message : "Unknown error");
                setLoading(false);
                setIsFetching(false);
            });
    }, [
        filters,
        segmentationDimension,
        ordersDayTimestamp,
        ordersEmailSearch,
        ordersProblemsPage,
        ordersCancelledPage,
        refreshToken,
    ]);

    const refresh = useCallback(() => {
        setRefreshToken(`${Date.now()}-${Math.random()}`);
    }, []);

    const exportCsv = useCallback(async () => {
        try {
            setExportError(null);
            setExportLoading(true);
            await ordersAnalyticsApi.downloadOrdersExportCsv(filters);
        } catch (reason) {
            setExportError(reason instanceof Error ? reason.message : "Unknown error");
        } finally {
            setExportLoading(false);
        }
    }, [filters]);

    return {
        data,
        loading,
        isFetching,
        error,
        refresh,
        exportCsv,
        exportLoading,
        exportError,
        lastRefreshedAt,
    };
}
